/**
 * Keep footer Quick links aligned with header pages:
 * a page that is on in the header is on in the footer; a page that is off is not shown.
 */

export interface FooterQuickLink {
  label: string;
  link: string;
  pageType?: string;
}

export interface FooterQuickLinkPage {
  name?: string;
  pageType?: string;
  type?: string;
  visible?: boolean;
  active?: boolean;
}

const PAGE_TYPE_ALIASES: Record<string, string> = {
  home: 'home',
  homepage: 'home',
  'express-wash-packages': 'express-wash-packages',
  'express-washes': 'express-wash-packages',
  'express-wash': 'express-wash-packages',
  'express-auto-details': 'express-auto-details',
  'express-details': 'express-auto-details',
  'express-detail': 'express-auto-details',
  'express-auto': 'express-auto-details',
  'fleet-management': 'fleet-management',
  fleet: 'fleet-management',
  fleets: 'fleet-management',
  coupons: 'coupons',
  coupon: 'coupons',
  'car-wash-coupons--way': 'coupons',
  'car-wash-coupons': 'coupons',
  memberships: 'memberships',
  membership: 'memberships',
};

const KNOWN_PAGE_TYPES = new Set([
  'home',
  'express-wash-packages',
  'express-auto-details',
  'fleet-management',
  'coupons',
  'memberships',
]);

function slugify(value: string): string {
  return (value || '')
    .replace(/^[#/]+/, '')
    .replace(/\/+$/, '')
    .toLowerCase()
    .trim();
}

export function normalizeFooterPageType(value: string | undefined | null): string {
  const slug = slugify(value || '');
  return PAGE_TYPE_ALIASES[slug] || slug;
}

export function isVisibleFooterPage(page: FooterQuickLinkPage): boolean {
  return page.visible !== false && page.active !== false;
}

function inferTypeFromLabel(label: string): string {
  const name = (label || '').toLowerCase().trim();
  if (name.includes('express wash')) {
    return 'express-wash-packages';
  }
  if (name.includes('express detail') || name.includes('express auto')) {
    return 'express-auto-details';
  }
  if (name.includes('fleet')) {
    return 'fleet-management';
  }
  if (name.includes('coupon')) {
    return 'coupons';
  }
  if (name.includes('membership')) {
    return 'memberships';
  }
  if (name === 'home' || name === 'homepage') {
    return 'home';
  }
  return '';
}

export function isKnownFooterPageType(type: string): boolean {
  return KNOWN_PAGE_TYPES.has(normalizeFooterPageType(type));
}

export function getFooterPageType(page: FooterQuickLinkPage): string {
  const fromFields = normalizeFooterPageType(page.pageType || page.type || '');
  if (fromFields && isKnownFooterPageType(fromFields)) {
    return fromFields;
  }
  const fromName = inferTypeFromLabel(page.name || '');
  if (fromName) {
    return fromName;
  }
  return fromFields || normalizeFooterPageType((page.name || '').replace(/\s+/g, '-'));
}

export function isExternalFooterHref(href: string | undefined | null): boolean {
  return /^(https?:\/\/|mailto:|tel:)/i.test((href || '').trim());
}

/** Keep staff-app preview URLs and absolute destinations as stored. */
export function shouldKeepStoredFooterHref(href: string | undefined | null): boolean {
  const value = (href || '').trim();
  if (!value) {
    return false;
  }
  if (isExternalFooterHref(value)) {
    return true;
  }
  return value.startsWith('/preview') || /[?&]pageType=/i.test(value);
}

export function inferFooterLinkPageType(link: FooterQuickLink | null | undefined): string {
  if (!link || typeof link !== 'object') {
    return '';
  }
  if (link.pageType) {
    return normalizeFooterPageType(String(link.pageType));
  }
  const href = (link.link || '').trim();
  // Website JSON stores homepage as `/`; that must not become the Angular app root.
  if (!href || href === '/' || href === '#') {
    return 'home';
  }
  const queryType = href.match(/[?&]pageType=([^&]+)/i);
  if (queryType) {
    try {
      return normalizeFooterPageType(decodeURIComponent(queryType[1]));
    } catch {
      return normalizeFooterPageType(queryType[1]);
    }
  }
  // `/preview?websiteId=...` with no pageType is the home preview route.
  if (/^\/preview(?:\?|$)/i.test(href)) {
    return 'home';
  }
  const hashOrPath = href.includes('#') ? href.slice(href.indexOf('#') + 1) : href;
  const pathSlug = slugify(hashOrPath.split('?')[0]);
  const fromPath = normalizeFooterPageType(pathSlug);
  return isKnownFooterPageType(fromPath) ? fromPath : '';
}

export function buildStaffPreviewFooterHref(
  pageType: string,
  search: string | URLSearchParams
): string | null {
  const params =
    typeof search === 'string' ? new URLSearchParams(search.replace(/^\?/, '')) : search;
  const websiteId = params.get('websiteId');
  const templateId = params.get('id') || params.get('templateId');
  if (!websiteId && !templateId) {
    return null;
  }
  const next = new URLSearchParams();
  if (websiteId) {
    next.set('websiteId', websiteId);
  } else if (templateId) {
    next.set('id', templateId);
  }
  const type = normalizeFooterPageType(pageType) || 'home';
  if (type && type !== 'home') {
    next.set('pageType', type);
  }
  return `/preview?${next.toString()}`;
}

export function remapFooterLinksForStaffPreview(
  links: FooterQuickLink[],
  pathname: string,
  search: string
): FooterQuickLink[] {
  if (!pathname.startsWith('/preview')) {
    return links;
  }
  return links.map(link => {
    if (isExternalFooterHref(link.link)) {
      return link;
    }
    const pageType = inferFooterLinkPageType(link);
    if (!pageType || !isKnownFooterPageType(pageType)) {
      return link;
    }
    const href = buildStaffPreviewFooterHref(pageType, search);
    return href ? { ...link, link: href, pageType } : link;
  });
}

export interface StaffPreviewLocation {
  pathname: string;
  search: string;
  assign: (url: string) => void;
}

/** Full navigation on /preview so footer clicks do not depend on theme event bubbling. */
export function navigateStaffPreviewFooterLink(
  link: FooterQuickLink,
  locationLike: StaffPreviewLocation
): boolean {
  if (!locationLike?.pathname?.startsWith('/preview')) {
    return false;
  }
  const pageType = inferFooterLinkPageType(link) || 'home';
  const href = buildStaffPreviewFooterHref(pageType, locationLike.search || '');
  if (!href) {
    return false;
  }
  const current = `${locationLike.pathname}${locationLike.search || ''}`;
  if (current === href) {
    return true;
  }
  locationLike.assign(href);
  return true;
}

export function handleFooterQuickLinkClick(
  event: Event,
  link: FooterQuickLink,
  emit: (pageType: string) => void,
  locationLike: StaffPreviewLocation | undefined = typeof window === 'undefined'
    ? undefined
    : window.location
): void {
  const href = (link?.link || '').trim();
  if (isExternalFooterHref(href)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  if (locationLike && navigateStaffPreviewFooterLink(link, locationLike)) {
    return;
  }
  emit(inferFooterLinkPageType(link) || 'home');
}

function applyStaffPreviewRemap(links: FooterQuickLink[]): FooterQuickLink[] {
  if (typeof window === 'undefined') {
    return links;
  }
  return remapFooterLinksForStaffPreview(links, window.location.pathname, window.location.search);
}

export function buildQuickLinksFromPages(
  pages: FooterQuickLinkPage[] | null | undefined
): FooterQuickLink[] {
  return (pages || []).filter(isVisibleFooterPage).map(page => {
    const pageType = getFooterPageType(page) || (page.name || '').toLowerCase().replace(/\s+/g, '-');
    return {
      label: page.name || '',
      link: `#${pageType}`,
      pageType,
    };
  });
}

/**
 * Footer links follow header pages: visible pages stay, hidden pages drop.
 * Custom (non-page) links are kept at the end.
 */
export function resolveFooterQuickLinks(
  storedLinks: FooterQuickLink[] | null | undefined,
  pages: FooterQuickLinkPage[] | null | undefined
): FooterQuickLink[] {
  try {
    const validStored = (storedLinks || []).filter(
      (link): link is FooterQuickLink => !!link && typeof link === 'object'
    );
    const allPages = pages || [];
    if (!allPages.length) {
      return applyStaffPreviewRemap(validStored);
    }

    const fromVisiblePages = buildQuickLinksFromPages(allPages);
    const visibleTypes = new Set(fromVisiblePages.map(link => inferFooterLinkPageType(link)));
    const storedByType = new Map<string, FooterQuickLink>();
    validStored.forEach(link => {
      const type = inferFooterLinkPageType(link);
      if (type && isKnownFooterPageType(type) && visibleTypes.has(normalizeFooterPageType(type))) {
        storedByType.set(normalizeFooterPageType(type), link);
      }
    });

    const pageLinks = fromVisiblePages.map(link => {
      const type = inferFooterLinkPageType(link);
      const stored = type ? storedByType.get(type) : undefined;
      if (!stored) {
        return link;
      }
      const storedHref = (stored.link || '').trim();
      if (shouldKeepStoredFooterHref(storedHref)) {
        return { ...stored, pageType: type || stored.pageType, label: stored.label || link.label };
      }
      // Bare `/` or `#` would leave the staff app (e.g. `/` → /dashboard).
      return { ...stored, link: link.link, pageType: type || stored.pageType };
    });

    const customLinks = validStored.filter(link => {
      const type = inferFooterLinkPageType(link);
      return !type || !isKnownFooterPageType(type);
    });

    return applyStaffPreviewRemap([...pageLinks, ...customLinks]);
  } catch {
    return applyStaffPreviewRemap(
      (storedLinks || []).filter(link => !!link && typeof link === 'object')
    );
  }
}
